// Data seam for the tenant landing page (mockup 20, "Inicio"). The owner's
// at-a-glance panorama: a weekly summary, three KPIs, the "needs your attention"
// queue, the account-manager card and quick links. Low density on purpose.
//
// Wired: getHome() -> GET /home, computed tenant-scoped from
// conversations (KPIs + attention) + the tenant's account manager (A-L5).
// Leads-dependent bits (the "prospectos calificados" KPI + the stale-lead
// attention row) are omitted until the leads domain exists (C-L4); the page
// renders whatever KPIs/attention the endpoint returns.

import { apiClient } from '../api/client.js'

export interface HomeKpi {
  id: string
  label: string
  value: string
  delta: { dir: 'up' | 'down' | 'flat'; text: string }
}

export type AttentionIcon = 'clock' | 'prospect' | 'team'
export interface AttentionItem {
  id: string
  tone: 'warn' | 'neutral'
  icon: AttentionIcon
  text: string
  meta: string
  /** Route the row navigates to. */
  to: string
}

export type QuickLinkIcon = 'inbox' | 'leads' | 'reports'
export interface QuickLink {
  id: string
  label: string
  icon: QuickLinkIcon
  to: string
}

export interface HomeData {
  summary: string
  kpis: HomeKpi[]
  attention: AttentionItem[]
  /** Who the account manager is. The card's blurb is composed web-side, from
   *  the same role flag that gates the "Escríbele" button (see Home.tsx). */
  manager: { name: string; initials: string }
  quickLinks: QuickLink[]
  /** The tenant is still in first-run (status 'onboarding', not yet live) — Home
   *  shows a "finish setup" banner linking to the /onboarding wizard. */
  onboardingPending: boolean
  /** Whether the agent is actually reachable on WhatsApp (a connected number +
   *  an active bot), so the owner sees at a glance that it's working. */
  agent: {
    live: boolean
    status: 'live' | 'no_channel' | 'no_active_bot'
    whatsappNumber: string | null
  }
}

/**
 * GET /home — the tenant-scoped Inicio panorama (summary, KPIs, attention
 * queue, account manager, quick links, agent liveness) in one call.
 */
export async function getHome(): Promise<HomeData> {
  const res = await apiClient.get<{ data: HomeData }>('/home')
  return res.data.data
}
